import * as React from 'react'
import { CSSProperties, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import Operations from '../../../operations'
import Selectors from '../../../selectors'
import { ConsentFormTemplate } from '@lmc/common/types'
import { colors, sizes } from '@lmc/common/constants'
import { history } from '../../../store'
import {
  Badge,
  EmptyState,
  HeaderSection,
  Icon,
  IconType,
  ListItem,
  Modal
} from '@lmc/ui-kit'
import {
  ConsentPaywall,
  ConsentPopover
} from '../../Residents/screens/ResidentConsentScreens/ResidentConsentFormsScreen/components'
import { Dropdown } from 'antd'
import { useTranslation } from 'react-i18next'
import { Home } from '@lmc/ts-common'
import { getNominative } from 'common/utils'

type ConsentTemplatesLibraryProps = {
  updateConsentTemplate: (
    consentTemplateID: string,
    isArchived: boolean,
    successMsg: string
  ) => Promise<void>
  hasCarePlans: boolean
  fetchTemplates: () => void
  templateSummaries: {
    titleContent: string
    isArchived: boolean
    subTitleContent: string
    iconContent: string
    state: 'signed' | 'unsigned' | 'archived' | 'refused'
    id: string
    type: 'consent' | 'lackOfCapacity'
  }[]
  preferredPeopleNominative?: string
}

export const ConsentTemplatesLibrary = ({
  updateConsentTemplate,
  fetchTemplates,
  templateSummaries,
  hasCarePlans,
  preferredPeopleNominative
}: ConsentTemplatesLibraryProps): React.ReactElement => {
  const { t } = useTranslation()
  useEffect(() => {
    fetchTemplates()
  }, [])

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)
  const [templateId, setTemplateId] = useState<string | undefined>(undefined)

  const handleArchive = (id: string) => {
    setIsModalVisible(true)
    setTemplateId(id)
  }

  const handleRestore = async (id: string) => {
    await updateConsentTemplate(id, false, translate('wasRestored'))
  }

  const handleView = (id: string) => {
    history.push(`/settings/consent-templates/${id}`)
  }

  const translate = (key: string) => t(`consent.consentTemplatesLibrary.${key}`)
  return hasCarePlans ? (
    <div style={styles.container}>
      <HeaderSection
        onClick={history.back}
        style={styles.header}
        title={translate('title')}
      />
      {templateSummaries.length ? (
        templateSummaries.map((template) => (
          <ListItem
            key={template.id}
            iconContent={template.iconContent}
            onClick={() => handleView(template.id)}
            rightChildren={
              <div onClick={(e) => e.stopPropagation()}>
                <Dropdown
                  overlay={
                    <div>
                      {' '}
                      <ConsentPopover
                        restore={
                          template.state === 'archived'
                            ? handleRestore
                            : undefined
                        }
                        archive={
                          template.state === 'archived'
                            ? undefined
                            : handleArchive
                        }
                        view={
                          template.state === 'archived' ? undefined : handleView
                        }
                        id={template.id}
                      />
                    </div>
                  }
                  placement="bottomRight"
                  trigger={['click']}
                >
                  <Icon
                    customIcon={IconType.Other}
                    style={styles.otherIcon as CSSProperties}
                  />
                </Dropdown>
              </div>
            }
            state={template.state}
            style={styles.listItem}
            titleContent={
              <div style={styles.listItemTitle}>
                <span>{template.titleContent}</span>
                {template.state === 'archived' ? (
                  <div style={styles.archived}>{translate('archived')}</div>
                ) : (
                  template.type === 'lackOfCapacity' && (
                    <Badge
                      color={colors.informationblue}
                      size="small"
                      style={styles.badge}
                      text={translate('lackOfCapacity')}
                      textColor={colors.white}
                    />
                  )
                )}
              </div>
            }
            subTitleContent={template.subTitleContent}
          />
        ))
      ) : (
        <EmptyState
          imageSrc="https://lmc-data-production.s3.eu-west-2.amazonaws.com/icons/sad_logan.png"
          title={translate('emptyState')}
        />
      )}
      <Modal
        localisation={{
          title: translate('modal.warning'),
          confirmText: t(`general.buttons.confirm`),
          cancelText: t(`general.buttons.cancel`)
        }}
        onConfirm={async () => {
          await updateConsentTemplate(
            // @ts-ignore
            templateId,
            true,
            translate('modal.wasArchived')
          )
          setIsModalVisible(false)
          fetchTemplates()
        }}
        onCancel={() => {
          setIsModalVisible(false)
          setTemplateId(undefined)
        }}
        setIsModalVisible={setIsModalVisible}
        isModalVisible={isModalVisible}
      >
        <>
          <div style={styles.archiveModalRow1}>{translate('modal.row1')}</div>
          <div style={styles.archiveModalRow2}>{translate('modal.row2')}</div>
        </>
      </Modal>
    </div>
  ) : (
    <ConsentPaywall
      preferredPeopleNominative={getNominative(
        preferredPeopleNominative,
        'service users'
      ).toLowerCase()}
    />
  )
}

const styles = {
  archiveModalRow1: {
    fontSize: 14
  },
  archived: {
    fontSize: 12,
    fontWeight: 300,
    color: colors.red,
    marginLeft: 8,
    marginTop: 4
  },
  archiveModalRow2: {
    fontWeight: 700,
    fontSize: 14
  },
  badge: {
    marginLeft: 8
  },
  container: {
    margin: '0 auto',
    maxWidth: sizes.childScreen,
    padding: '24px 24px'
  },
  header: {
    paddingBottom: 24
  },
  listItem: {
    marginBottom: 8
  },
  listItemTitle: {
    display: 'flex',
    alignItems: 'center'
  },
  otherIcon: {
    float: 'right',
    cursor: 'pointer',
    marginLeft: 16,
    width: 20,
    height: 20
  }
}

const mapStateToProps = (state: {
  data: {
    templates: ConsentFormTemplate[]
  }
  global: {
    home: Home
  }
}) => ({
  templateSummaries: Selectors.getHomeTemplates(state),
  hasCarePlans: state.global?.home?.features?.carePlans?.enabled,
  preferredPeopleNominative: state.global.home.preferredPeopleNominative
})

const mapDispatchToProps = (dispatch: any) => ({
  updateConsentTemplate: async (
    consentTemplateID: string,
    isArchived: boolean,
    successMsg: string
  ) => {
    await Operations.updateListItem({
      listId: 'consent-templates',
      itemId: consentTemplateID,
      json: { isArchived },
      successMsg
    })(dispatch)
    await Operations.loadList('consent-templates')(dispatch)
  },
  fetchTemplates: () => {
    dispatch(Operations.loadList('consent-templates'))
  }
})

export default connect<any, any, any>(
  // @ts-ignore
  mapStateToProps,
  mapDispatchToProps
)(ConsentTemplatesLibrary)
